import { useCallback, useState } from 'react';
import { cognitoChangePassword } from '../../pages/login/cognito';

interface UseChangePasswordReturn {
  changePassword: (
    accessToken: string,
    currentPassword: string,
    newPassword: string,
  ) => Promise<{ success: boolean; error?: string }>;
  isProcessing: boolean;
}

/**
 * Hook to handle password change operations with AWS Cognito.
 * Platform-agnostic: Can be used in React Native.
 *
 * Manages:
 * - Password change API call to Cognito
 * - Processing state
 * - Error handling and user-friendly messages
 *
 * @returns Password change operation and processing state
 */
export function useChangePassword(): UseChangePasswordReturn {
  const [isProcessing, setIsProcessing] = useState(false);

  const changePassword = useCallback(
    async (accessToken: string, currentPassword: string, newPassword: string) => {
      setIsProcessing(true);
      try {
        const result = await cognitoChangePassword(
          'eu-central-1',
          accessToken,
          currentPassword,
          newPassword,
        );

        setIsProcessing(false);

        if (result.success) {
          return { success: true };
        }

        // Map error codes to user-friendly messages
        let errorMessage: string;
        switch (result.error) {
          case 'WrongPassword':
            errorMessage = 'Current password is incorrect';
            break;
          case 'InvalidPassword':
            errorMessage = 'New password does not meet requirements';
            break;
          case 'LimitExceeded':
            errorMessage = 'Too many attempts. Please try again later';
            break;
          case 'NotAuthorized':
            errorMessage = 'Not authorized to change password';
            break;
          default:
            errorMessage = 'Failed to change password. Please try again';
        }

        return { success: false, error: errorMessage };
      } catch (err) {
        setIsProcessing(false);
        return { success: false, error: 'An unexpected error occurred' };
      }
    },
    [],
  );

  return {
    changePassword,
    isProcessing,
  };
}
